import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EP = { leads: `${API}/leads.php` };

const GOALS = [
  { value: "wealth", label: "Grow Wealth" },
  { value: "insurance", label: "Protect Family" },
  { value: "tax", label: "Save Tax" },
  { value: "other", label: "Other" },
];

export default function LeadCaptureModal({ open, onOpenChange }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", goal: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const selectGoal = (value) => {
    setForm((prev) => ({ ...prev, goal: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(EP.leads, form);
      setSubmitted(true);
      toast.success("Thank you! We'll connect with you shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (val) => {
    if (!val) {
      setSubmitted(false);
      setForm({ name: "", phone: "", email: "", goal: "" });
    }
    onOpenChange(val);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="lead-capture-modal">
        {submitted ? (
          <div className="py-8 text-center space-y-4">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
            <p className="font-['Outfit'] font-semibold text-lg text-[#0B1F3A]">
              Thank you, {form.name}!
            </p>
            <p className="text-sm text-gray-500">
              We'll get in touch with you within 15 minutes.
            </p>
            <div className="flex flex-col gap-2 pt-2">
              <a
                href={`whatsapp://send?phone=919324555493&text=${encodeURIComponent(`Hi Sagar, I'm ${form.name}. I need help with ${GOALS.find(g => g.value === form.goal)?.label || "financial planning"}. Please get in touch at ${form.phone}.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold px-6 py-3 rounded-full text-sm hover:bg-[#20BD5A] transition-all"
                data-testid="lead-success-whatsapp"
              >
                <MessageCircle className="w-4 h-4" fill="white" />
                Chat on WhatsApp for Faster Response
              </a>
              <button
                onClick={() => handleClose(false)}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                data-testid="lead-success-close"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-['Outfit'] text-xl text-[#0B1F3A]">
                Get Your Free Financial Plan
              </DialogTitle>
              <DialogDescription className="text-gray-500">
                Share your details and we'll connect within 15 minutes.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="lead-form">
              <div>
                <Input
                  name="name"
                  placeholder="Your Name *"
                  value={form.name}
                  onChange={handleChange}
                  className="rounded-xl h-11"
                  data-testid="lead-form-name"
                  required
                />
              </div>
              <div>
                <Input
                  name="phone"
                  type="tel"
                  placeholder="Phone Number *"
                  value={form.phone}
                  onChange={handleChange}
                  className="rounded-xl h-11"
                  data-testid="lead-form-phone"
                  required
                />
              </div>
              <div>
                <Input
                  name="email"
                  type="email"
                  placeholder="Email Address *"
                  value={form.email}
                  onChange={handleChange}
                  className="rounded-xl h-11"
                  data-testid="lead-form-email"
                  required
                />
              </div>

              {/* Goal selector */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">What's your goal?</p>
                <div className="grid grid-cols-2 gap-2">
                  {GOALS.map((g) => (
                    <button
                      key={g.value}
                      type="button"
                      onClick={() => selectGoal(g.value)}
                      className={`text-sm px-3 py-2.5 rounded-xl border transition-all ${
                        form.goal === g.value
                          ? "border-[#3B82F6] bg-[#3B82F6]/5 text-[#3B82F6] font-medium"
                          : "border-gray-200 text-gray-600 hover:border-gray-300"
                      }`}
                      data-testid={`lead-form-goal-${g.value}`}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold shadow-md btn-glow"
                data-testid="lead-form-submit"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Submit & Get Free Plan"
                )}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
